import { Divider, Grid, Typography, TextField as MuiTextField, Stack, Box, IconButton, MenuItem, Chip, 
  Autocomplete, AutocompleteChangeReason, Link, Tooltip, FormControlLabel, Checkbox, InputAdornment, 
  FormHelperText, AutocompleteCloseReason } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { FC, useRef, useState, useCallback, useEffect, MutableRefObject, useMemo } from "react";
import { SimpleOption, useAmbientFacilitiesContext } from "../search/AmbientFacilitiesContext";
import { DataCatalogResponseDto, EncryptMappingStatus, ExtraIgnoreItem, MaskingRule, PrimaryClassification, SecondaryClassificationResponseDto, UpdateFieldGovernanceInput } from "~/gen/api/governance";
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext";
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import SelectField from "../../formik/SelectField";
import { useRecordContext } from "ra-core";
import { RecPatch } from "~/dataProvider/tools";
import { useFieldEditContext } from "./FieldEditContext";
import { DataTypeMaskingKind, integerRangeCheck, nonIntegerRangeCheck } from "./maskingRuleDataTypesMap";
import { useEncryptedDatabasesContext } from "~/components/databases/useEncryptedDatabases";
import { UpgradeStatus } from "~/gen/api/encrypt";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG, MANAGE_DATA_CLASSIFICATIONS } from "~/constants";
import { disabledColor } from "~/utils/a11y";



const validationSchema =
  Yup.object().shape({
    alias: Yup.string().max(100),
    description: Yup.string().max(512),
    // primaryClassification: Yup.number(),
    secondaryClassificationId: Yup.number(),
    // encryptionRuleId: Yup.number(),
    // maskingRule: Yup.number(),
    retentionRuleId: Yup.number(),
    reasonCollectId: Yup.number(),
  })


export interface FieldEditProps {
  handleSubmitRef: MutableRefObject<()=>void>,
}


interface FieldEditValues {
  submit: string;
  alias: string;
  description: string;
  primaryClassification: PrimaryClassification | undefined;
  secondaryClassificationId: number | undefined;
  encryptionRuleId: string | number;
  maskingRule: '' | MaskingRule;
  maskingValue: string;
  retentionRuleId: string | number;
  reasonCollectId: string | number;
}

const validatorBuilder = (mode: 'single'|'bulk', maskingKind: DataTypeMaskingKind, datatype?:string, precision?:number, scale?:number) => (values: FieldEditValues) => {
  const errorBag = {} as Record<string, string>
    if (values.maskingRule !== '' && values.maskingRule !== MaskingRule.None) {
      if (maskingKind === 'date') {
        if (!/\d{4}-\d{2}-\d{2}$/.test(values.maskingValue.trim())
        || isNaN(Date.parse(values.maskingValue.trim()))
        ) {
          errorBag['maskingValue'] = 'Invalid date string'
        }
      } else if (maskingKind === 'integer_num') {
        const mv = values.maskingValue.trim()
        if (!/^-?\d+$/.test(mv)) {
          errorBag['maskingValue'] = 'Invalid masking value'
        } else if (!integerRangeCheck(mv, datatype || '')) {
          errorBag['maskingValue'] = 'Invalid masking value (numeric range)'
        }
      } else if (maskingKind === 'non_integer_num') {
        const mv = values.maskingValue.trim()
        const i = mv.indexOf('.')
        if (isNaN(Number.parseFloat(mv)) 
        || (i > -1 && !/^\d*$/.test(mv.substring(i + 1)))
        || (i === -1 && !/^-?\d+(.\d+)?$/.test(mv))
        ) {
          errorBag['maskingValue'] = 'Invalid masking value'
        } else if (!nonIntegerRangeCheck(mv, datatype || '', precision, scale)) {
          errorBag['maskingValue'] = 'Invalid masking value (numeric range or scale)'
        }
      } 
    }


    if (Object.keys(errorBag).length > 0) {
      return errorBag
    }
}


const FieldEdit: FC<FieldEditProps> = ({ handleSubmitRef }) => {  
  const hasDCWritePermit = useHasPermit([MANAGE_DATA_CATALOG]),
    hasClssEncryptPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION]),
    hasManageClssPermit  = useHasPermit([MANAGE_DATA_CLASSIFICATIONS])
  const preData = useRecordContext<DataCatalogResponseDto&RecPatch>()
  const { goMaintenancePage, bulkEditMaskingKind:{datatype} } = useDataCatalogContext()
  const { retentionRules, reasonCollects, tagOptions, classifications, tagCreate } = useAmbientFacilitiesContext()
  const { save, encryptRules, maskingKind, mode } = useFieldEditContext()
  const encryptedDbMap = useEncryptedDatabasesContext().encryptedDbMap || {}
  const escLocked = 
    !!preData && encryptedDbMap[preData.databaseId || -1] 
    && encryptedDbMap[preData.databaseId || -1].upgradeStatus != UpgradeStatus.Unlocked
  
  const noBulk = Boolean(preData?.fieldId)
  const noBulk_n_shouldNotEncrypt = Boolean(preData?.fieldId && (preData.isIdentity || preData.isComputed || preData.extraIgnoreItem !== ExtraIgnoreItem.None))
  const initialValues = {
    submit: '',
    alias: preData?.alias || '',
    description: preData?.description || '',
    primaryClassification: preData?.primaryClassification,
    secondaryClassificationId: preData?.secondaryClassificationId || undefined,
    encryptionRuleId: 
      preData 
        ? (preData.encryptMappingStatus === EncryptMappingStatus.Skipped ? '-skip-' 
        : preData.encryptionRuleId || '') 
        : '',
    maskingRule: 
      preData
      ? preData.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour ? MaskingRule.ReplaceWithCharacter
      : ((preData.maskingRule !== undefined && preData.maskingRule !== null) ? preData.maskingRule : '')
      : '' as MaskingRule|'',
    maskingValue: preData?.maskingValue || '',
    retentionRuleId: preData?.retentionRuleId || '',
    reasonCollectId: preData?.reasonCollectId || '',
    ...(noBulk_n_shouldNotEncrypt && {
      encryptionRuleId: undefined,
      maskingRule: '' as const,
      maskingValue: undefined,
    }),
  }

  const [states, setStates] = useState({
    preserveLast4: preData?.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour,
    cls: preData?.secondaryClassificationId ? (classifications.find(c => c.key === preData.secondaryClassificationId) || null) : null,
    hideRetentionRule: !initialValues.retentionRuleId && !initialValues.reasonCollectId,
    tagSearch: '',
    tagWarning: '',
    selectedTags: [] as SimpleOption[],
  })

  const validate = useMemo(() =>
    validatorBuilder(mode, maskingKind, mode === 'single' ? (preData?.type || '') : datatype, preData?.precision || undefined, preData?.scale || undefined),
    [mode, maskingKind, preData?.precision, preData?.scale]
  )

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (!hasDCWritePermit) { return }
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const payload: UpdateFieldGovernanceInput = {
      ...values,
      tags: states.selectedTags.map(t => t.key),
      encryptMappingStatus: 
        values.encryptionRuleId === '-skip-' ? EncryptMappingStatus.Skipped
        : !values.encryptionRuleId ? EncryptMappingStatus.PendingReview
        : EncryptMappingStatus.Mapped,
      encryptionRuleId: 
        ['-skip-', ''].indexOf(values.encryptionRuleId as any) > -1 ? undefined : values.encryptionRuleId,
      maskingRule: 
        values.maskingRule === '' ? undefined
        : values.maskingRule === MaskingRule.ReplaceWithCharacter ? (
          states.preserveLast4 ? MaskingRule.ReplaceWithCharacterPreserveLastFour : MaskingRule.ReplaceWithCharacter)
        : values.maskingRule,
      maskingValue: values.maskingRule !== '' && values.maskingRule !== 0 ? values.maskingValue.trim() : undefined,
      retentionRuleId: values.retentionRuleId || undefined,
      reasonCollectId: values.reasonCollectId || undefined,
      ...(noBulk_n_shouldNotEncrypt && {
        encryptionRuleId: undefined,
        maskingRule: undefined,
        maskingValue: undefined,
      }),
    } as any
    save(payload, options)
  }, [save, states.preserveLast4, states.selectedTags, preData, hasDCWritePermit])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, values, handleSubmit, setFieldValue, setValues } = formikCtrl
  handleSubmitRef.current = handleSubmit

  const complianceCount = [states.cls, values.retentionRuleId, values.reasonCollectId].map(x => (!!x ? 1 : 0) as number).reduce((acc, i) => acc + i)
  const indicatorColor = ['#FFB034', '#FFB034', '#FFB034', 'green'][complianceCount]

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications
    ]
  }, [classifications])
  
  const componentLoaded = useRef(false)
  useEffect(() => {
    if (componentLoaded.current) {
      setStates(v => ({...v,
        preserveLast4: preData?.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour
      }))
      setFieldValue('maskingValue', preData?.maskingValue || '')
    }
    componentLoaded.current = true
  }, [values.maskingRule])

  useEffect(() => {
    if (noBulk && tagOptions.length > 0) {
      setStates(v => ({...v,
        selectedTags: (preData.tags ?? []).map(t => tagOptions.find(o => o.key === t)).filter(x => !!x) as SimpleOption[],
      }))
    }
  }, [preData, tagOptions, noBulk])

  const onTagSelectionChange = (event:any, value: any, reason:AutocompleteChangeReason, detail: any) => {
    if (!hasDCWritePermit) { return }
    if (reason === 'selectOption') {  
      const newOption = value as SimpleOption
      if (states.selectedTags.every(x => x.key !== newOption.key)) {
        setStates(v => ({...v, selectedTags: [...v.selectedTags, newOption], tagSearch: '' }))
      }
    } else if (reason === 'createOption') {
      const sValue = value as string
      tagCreate(sValue, {
        onSuccess: ({data}) => {
          setStates(v => ({...v, 
            selectedTags: [...v.selectedTags, {...data, key: data.id, text: sValue, } as SimpleOption],
            tagSearch: '',
          }))
        },
      })
    }
  }
  
  const handleTagDelete = (t:SimpleOption) => {
    const i = states.selectedTags.findIndex(x => x.key === t.key)
    if (i > -1) {
      setStates(v => {
        v.selectedTags.splice(i, 1)
        return {...v, selectedTags: v.selectedTags }
      })
    }
  }

  const handleTagInputBlur = (event:any, reason:AutocompleteCloseReason) => {
    if ((reason === 'blur' || reason === 'escape') && event.target.value) {
      setStates(v => ({...v, tagWarning: 'Type "Enter" key to finish Tag creation' }))
    }
  }

  return (
    <OdForm formikCtrl={formikCtrl}>
    
      {errors.submit && (
        <Box sx={{ mt: 3, mb: 2, mx: 4 }}>
          <FormHelperText error>
            {errors.submit}
          </FormHelperText>
        </Box>
      )}
      <Grid 
        container 
        sx={{ 
          py: 2, 
          px: 4,
          '& .MuiTypography-h6': { mt: 4 },
          '& .MuiDivider-root': { mb: 2 },
          '& div.MuiGrid-container.s1': {
            '& > div.MuiBox-root:first-of-type': {
              flex: '1 1 100%',
            },
            '& > div.MuiGrid-item': {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              mb: 3,
              '& .MuiBox-root.f1': { width: 360, },
              '& .f2': {
                flex: '1 1 auto',
                ml: 0.5,
              },
            },
          }, 
          ...((!hasDCWritePermit || !hasClssEncryptPermit) && {
            '& input.Mui-disabled, & div.Mui-disabled, & textarea.Mui-disabled': disabledColor
          })
        }}
      >
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={6}>
              <Stack direction="row">
                <Box sx={{ pr: 4, width: '50%', }}>
                  <Autocomplete 
                    disabled={!hasDCWritePermit}
                    freeSolo={true}
                    options={tagOptions}
                    inputValue={states.tagSearch}
                    getOptionLabel={(x) => (x.text || x) as string}
                    renderInput={(params) =>                  
                      <MuiTextField 
                        placeholder="Add Tag..." 
                        {...params} 
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': "add tag" 
                        }}
                        fullWidth 
                        error={!!states.tagWarning} 
                        helperText={states.tagWarning} />
                    }

                    onChange={onTagSelectionChange}
                    onInputChange={(event:any, value:string) => { setStates(v => ({...v, tagSearch: value, tagWarning: '' })) }}
                    onClose={handleTagInputBlur}
                  />
                </Box>
                  
                <Box sx={{ pr: 4, width: '50%', }}>
                  <TextField label="Alias" name="alias" fullWidth disabled={!hasDCWritePermit} />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Description" name="description" fullWidth multiline disabled={!hasDCWritePermit} />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1, '& .MuiChip-root': { mr: 2 } }}>
              {states.selectedTags.map(t => 
                <Chip key={t.key} label={t.text} variant="outlined" onDelete={hasDCWritePermit ? (() => handleTagDelete(t)) : undefined} />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container className="s1">
            <Box>
              <Typography variant="h6">
                Compliance
              </Typography>
              <Divider />
            </Box>
            <Grid item xs={6}>
              <Box className="f1">
                <Autocomplete 
                  disabled={!(hasDCWritePermit && hasClssEncryptPermit) || escLocked}
                  disableClearable={true}
                  options={classificationSelectOptions}
                  getOptionLabel={(option) => option.text}
                  value={states.cls as any}
                  
                  renderInput={((params) =>
                    <MuiTextField label="Classification" {...params} fullWidth />
                  )}
                  onChange={(event, value, reason) => {
                    if (hasDCWritePermit && hasClssEncryptPermit && reason === 'selectOption') {
                      if ((value as any)?.value === null) { // choosed '[None]'
                        setStates(v => ({...v, cls: null }))
                        setValues(v => ({...v,
                          primaryClassification: -1 as any,
                          secondaryClassificationId: -1, // means to remove classification; special care should be taken at API side
                          encryptionRuleId: '-skip-',
                          maskingRule: MaskingRule.None,
                          maskingValue: '',
                        }))
                      } else {
                        const cls1 = value as SecondaryClassificationResponseDto
                        setStates(v => ({...v, cls: value }))
                        setValues(v => (cls1.primaryClassification === PrimaryClassification.Public
                          ? {...v,
                              primaryClassification: cls1.primaryClassification,
                              secondaryClassificationId: cls1.secondaryClassificationId,
                              ...((!preData || (!preData.isIdentity && !preData.isComputed && !preData.extraIgnoreItem)) && {
                                encryptionRuleId: '-skip-',
                                maskingRule: MaskingRule.None,
                                maskingValue: '',
                              })
                            }
                          : {...v,
                              primaryClassification: cls1.primaryClassification,
                              secondaryClassificationId: cls1.secondaryClassificationId,
                            }
                        ))
                      }
                    }
                  }}
                />
              </Box>
              {hasDCWritePermit && hasManageClssPermit &&
                <Box className="f2">
                  <Tooltip title="Add New">
                    <IconButton onClick={() => goMaintenancePage('classification')}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>}
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  width: '100%',
                  mb: 'auto',
                  '& .MuiBox-root': {
                    width: 32,
                    height: 4,
                    bgcolor: indicatorColor,
                    float: 'right',
                    ml: 0.5,
                    '&.incomplete': {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                <Box className={complianceCount === 3 ? '' : 'incomplete'}></Box>
                <Box className={complianceCount >= 2 ? '' : 'incomplete'}></Box>
                <Box className={complianceCount > 0 ? '' : 'incomplete'}></Box>
              </Box>
            </Grid>
            {states.hideRetentionRule ?
              (hasDCWritePermit &&
              <Grid item xs={6}>
                <Link component="button" variant="body2" onClick={() => setStates(v => ({...v, hideRetentionRule: false }))}>
                  + Retention Rule & Reason for Collect
                </Link>
                <Tooltip title="These are strongly recommended to be filled out for complying to regulations like: GDPR">
                  <Box sx={{ ml: 2, cursor: 'pointer' }}>
                    <QuestionMarkIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              </Grid>) :
              <>
                <Grid item xs={6}>
                  <Box className="f1">
                    <SelectField
                      disabled={!hasDCWritePermit}
                      label="Retention Rule"
                      name="retentionRuleId"
                    >
                      <MenuItem value="" >
                        <Typography variant="caption" color="InactiveCaptionText">-- empty --</Typography>
                      </MenuItem>                    
                      {retentionRules.map(r => 
                        <MenuItem key={r.retentionRuleId || 0} value={r.retentionRuleId} >{r.name}</MenuItem>                    
                      )}
                    </SelectField>
                  </Box>
                  {hasDCWritePermit &&
                    <Box className="f2">
                      <Tooltip title="Add New">
                        <IconButton onClick={() => goMaintenancePage('retentionRule')}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>}
                </Grid>
                <Grid item xs={6}>
                  <Box className="f1">
                    <SelectField
                      disabled={!hasDCWritePermit}
                      label="Reason for Collect"
                      name="reasonCollectId"
                    >
                      <MenuItem value="" >
                        <Typography variant="caption" color="InactiveCaptionText">-- empty --</Typography>
                      </MenuItem>                    
                      {reasonCollects.map(r =>
                        <MenuItem key={r.reasonCollectId || 0} value={r.reasonCollectId} >{r.reasonText}</MenuItem>                    
                      )}
                    </SelectField>
                  </Box>
                  {hasDCWritePermit &&
                    <Box className="f2">
                      <Tooltip title="Add New">
                        <IconButton onClick={() => goMaintenancePage('reason4collect')}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>}
                </Grid>
              </>
            }
          </Grid>

          <Grid container className="s1">
            <Box>
              <Typography variant="h6">
                {"Encryption & Masking"}
              </Typography>
              <Divider />
            </Box>
            {noBulk_n_shouldNotEncrypt
              ? (
                <Typography variant="caption" sx={{ mb: 1 }}>Not Applicable for {
                  preData.isIdentity || preData.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity 
                    ? "Identity" : "Computed"} Column</Typography>
              )
              : (<>

                  <Grid item xs={6}>
                    <Box className="f1">
                      <Tooltip 
                        title={
                          !states.cls ? "Choose Classification before designating Encryption Rule"
                          : ""
                        }
                      >
                        <SelectField
                          disabled={!(hasDCWritePermit && hasClssEncryptPermit)}
                          label="Encryption Rule"
                          name="encryptionRuleId"
                          readOnly={Boolean(!states.cls || escLocked || noBulk_n_shouldNotEncrypt)}
                        >
                          <MenuItem value={'-skip-'} >Skip</MenuItem>                    
                          {states.cls && (!!preData?.encryptionRuleId || (states.cls as SecondaryClassificationResponseDto).primaryClassification !== PrimaryClassification.Public )
                          && encryptRules.map(r => 
                            <MenuItem key={r.id} value={r.id} >{r.name}</MenuItem>                    
                          )}
                        </SelectField>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className="f1">
                      <SelectField
                        disabled={!(hasDCWritePermit && hasClssEncryptPermit)}
                        label="Masking Rule"
                        name="maskingRule"
                        readOnly={Boolean(escLocked || noBulk_n_shouldNotEncrypt)}
                      >
                        <MenuItem value={MaskingRule.None}>None</MenuItem>
                        {maskingKind === 'string' ?
                          <MenuItem value={MaskingRule.ReplaceWithCharacter}>Replace With Character</MenuItem>
                          : maskingKind === 'date' ?
                          <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Date</MenuItem>
                          : (maskingKind === 'integer_num' || maskingKind === 'non_integer_num') ?
                          <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Value</MenuItem>
                          : null
                        }
                      </SelectField>
                    </Box>
                    <Box className="f2" sx={{ ml: '32px !important' }}>
                      {(values.maskingRule === MaskingRule.ReplaceWithCharacter 
                      || values.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour) 
                      && values.maskingValue &&
                        <Typography variant="caption" color="InactiveCaptionText">
                          i.e: {
                            "".padStart(9 - (states.preserveLast4 ? 4 : 0), values.maskingValue[0]) + 
                            (states.preserveLast4 ? "1234" : "")
                          }
                        </Typography>
                      }
                    </Box>
                  </Grid>
                  {values.encryptionRuleId && ((() => {
                    const rule = encryptRules.find(r => r.id === values.encryptionRuleId)
                    if (!rule) { return null }
                    return (
                      <Grid item xs={6} sx={{}} >
                        <Box 
                          sx={{  
                            position: 'relative',
                            top: -16,
                            left: 16,
                            '& > table.sample td': { p: 0, pr: 2,
                              '&:after': { content: 'none' },
                            },
                          }}
                        >
                          <table className="sample">
                            <tr>
                              <td><Typography sx={{ color: 'primary.light' }} variant="caption">Input Sample:</Typography></td>
                              <td><Typography sx={{ color: 'primary.light' }} variant="caption">{rule.sampleInput}</Typography></td>
                            </tr>
                            <tr>
                              <td><Typography sx={{ color: 'success.light' }} variant="caption">Output Sample:</Typography></td>
                              <td><Typography sx={{ color: 'success.light' }} variant="caption">{rule.sampleOutput}</Typography></td>
                            </tr>
                          </table>
                        </Box>
                      </Grid>
                    )
                  })())
                  }
                  {!!values.maskingRule && values.maskingRule > 0 &&
                    <Grid item xs={6} sx={{ ml: 'auto' }}>
                      {values.maskingRule !== 3 ?
                        <>
                          <Box sx={{ width: 160, mr: 6 }} >
                            <TextField
                              disabled={!(hasDCWritePermit && hasClssEncryptPermit) || 
                                escLocked || noBulk_n_shouldNotEncrypt}
                              select
                              label=""
                              name="maskingValue"
                              InputProps={{
                                startAdornment: <InputAdornment position="start" sx={{mr:2}}>Character:</InputAdornment>
                              }}
                            >
                              {(maskingKind === 'string' ? ['*', '-'] : []).map(s => 
                                <MenuItem key={s} value={s}>{s}</MenuItem>                          
                              )}
                            </TextField>
                          </Box>
                          <FormControlLabel label="Preserve Last 4" 
                            control={
                              <Checkbox disabled={escLocked || noBulk_n_shouldNotEncrypt}
                                checked={states.preserveLast4} 
                                onChange={(e) => { setStates(v => ({...v, preserveLast4: (e.target.checked as any) })) }} 
                              />
                            } 
                          />
                        </> :
                        <Box className="f1">
                          <TextField 
                            disabled={!(hasDCWritePermit && hasClssEncryptPermit) || 
                              escLocked || noBulk_n_shouldNotEncrypt}
                            label="Masking Value" 
                            name="maskingValue"
                            helperText={
                              maskingKind === 'date' ? "Date in format like: 1970-01-01"
                              : ((maskingKind === 'integer_num' || maskingKind === 'non_integer_num') 
                                ? (numericMaskHelperTexts[(mode === 'single' ? (preData?.type) : datatype) || ''] || errors.maskingValue)
                                : errors.maskingValue
                                )
                            }
                            autoComplete="off"
                          />
                        </Box>
                      }
                    </Grid>
                  }
                </>)
            }
          </Grid>

          {/* <Grid container className="s1">
            <Box>
              <Typography variant="h6">
                Master Data Management
              </Typography>
              <Divider />
            </Box>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField label="Master Data" fullWidth />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField label="Rule" fullWidth />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField placeholder="Pick Related Fields..." fullWidth />
              </Box>
            </Grid>
          </Grid>

          <Grid container className="s1">
            <Box>
              <Typography variant="h6">
                KPI for Business Dashboard
              </Typography>
              <Divider />
            </Box>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField label="Alert Rule" fullWidth />
              </Box>
              <Box className="f2">
                <IconButton>
                  <EditIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField label="Visualization Type" fullWidth />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="f1">
                <TextField label="Rollup Rule" fullWidth />
              </Box>
              <Box className="f2">
                <IconButton>
                  <EditIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid> */}
        </Grid>
      </Grid>
    </OdForm>
  )
}

export default FieldEdit;

export const numericMaskHelperTexts = {
  'bigint':    'Range: [-9223372036854775808, 9223372036854775807]',
  'bigserial': 'Range: [1, 9223372036854775807]',
  'int':       'Range: [-2147483648, 2147483647]',
  'integer':   'Range: [-2147483648, 2147483647]',
  'mediumint': 'Range: [-8388608, 8388607]',
  'serial':    'Range: [1, 2147483647]',
  'smallint':    'Range: [-32768, 32767]',
  'smallserial': 'Range: [1, 32767]',
  'tinyint': 'Range: [0, 127]',
  'year': 'Range: [1901, 2155]',
  'money':      'Range: [-922337203685477.5808, 922337203685477.5807]',
  'smallmoney': 'Range: [-214748.3648, 214748.3647]',
} as Record<string, string>



